import { Checkbox, FontIcon, Icon, IIconProps, ILabelStyles, ILinkStyles, ISearchBoxStyles, IStackItemStyles, IStackTokens, Label, Link, mergeStyles, SearchBox, Slider, Stack, StackItem, TooltipHost } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import dayjs from "dayjs";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { setContentManagerFilterSectionItem, setContentManagerFilterSectionItems, setContentManagerFilterSectionItemsFromRange, setContentManagerFilterSectionToggleShowMOre, setContentManagerFilterSectionToggleSlider } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { toggleReload } from "../../../src/taskpane/redux/modules/reload";
import { isEmptyOnlyTitle } from "./filterSectionDisplay";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { CancellableSharePointRestApi } from "../../../src/taskpane/services/CancellableSharePointRestApi";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { DatasourceHelper } from "../../../src/taskpane/components/helpers/dataSourceHelper";
import { useClientTypeContext } from "../../../src/taskpane/components/hooks/useClientTypeContext";
import { Filter20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Filter20Regular";
import { FilterDismiss20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FilterDismiss20Regular";

export interface DateRangeStep {
  index: number;
  label: string;
  daysOffset?: number;
}

const checkboxLabelStyles: IStackItemStyles = {
  root: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  }
}

const labelStyles: ILabelStyles = {
  root: { marginBottom: 10 }
}

const linkStyles: ILinkStyles = {
  root: { marginTop: 10 }
}

const filterButtonStackItemStyles: IStackItemStyles = {
  root: {
    marginTop: -10
  }
}

const searchIcon: IIconProps = { iconName: "Search" };
const searchBoxStyles: Partial<ISearchBoxStyles> = {
  root: { height: "100%", marginBottom: 10, paddingBottom: 5 }
};

const horizontalGapStackTokens: IStackTokens = {
  childrenGap: 10
};

export interface CMFileFilterPanelSectionProps {
  filterSections: FilterSection[];
  filterSection: FilterSection;
  isInOneDrive: boolean;
  serverRelPath: string;
  oneDriveRootDetails: IOneDriveRootDetails;
  currentDirectory: IPageSettings;
  isInContentManagerMode: boolean;
  setFilterSectionItem?: (filterSection: FilterSection, sectionItem: FilterItem, isChecked: boolean) => void;
  setFilterSectionItems?: (filterSection: FilterSection, sectionItems: FilterItem[]) => void;
  setFilterSectionItemsFromRange?: (filterSection: FilterSection, daysOffset: number) => void;
  setFilterSectionToggleSlider?: (filterSection: FilterSection, skipApiCall?: boolean) => void;
  setFilterSectionToggleShowMore?: (filterSection: FilterSection) => void;
  // Replaces the built-in single-directory value search. When absent, the section
  // queries the browsed directory exactly as before (legacy call sites pass nothing).
  searchFilterValues?: (fieldName: string, searchValue: string) => Promise<string[]>;
  /**
   * "The user wants to see this section's values" — raised when they open the section's
   * search box or click Show more. Lets a host fetch the field's real distinct values
   * lazily, instead of offering only what the loaded rows happened to contain.
   *
   * Optional, in the same spirit as searchFilterValues: only the aggregate panel passes
   * it. The library and add-in panels pass nothing and behave exactly as before, because
   * they already build their sections from RenderListFilterData up front.
   */
  onRequestFilterValues?: (fieldName: string) => void;
}

export interface FilterSectionItemsExpandSearchParams {
  field: string;
  value: string;
}

// Duration of the section's max-height expand/collapse transition. Shared by the
// CSS below and the Show-less unmount delay so the two can never drift.
const COLLAPSE_ANIMATION_MS = 300;

export const CMFileFilterPanelSection = ({ filterSections, filterSection, isInOneDrive, serverRelPath, oneDriveRootDetails, currentDirectory, isInContentManagerMode, setFilterSectionItem, setFilterSectionItems, setFilterSectionItemsFromRange, setFilterSectionToggleSlider, setFilterSectionToggleShowMore, searchFilterValues, onRequestFilterValues }: CMFileFilterPanelSectionProps) => {
  const dispatch = useDispatch();

  const languageStrings = React.useContext(LanguageContext);

  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const autoOpenFilterSearchbox = useSelector((state: RootState) => state.tenantInfo?.autoOpenFilterSearchbox);
  const clientTypes = useClientTypeContext(currentDirectory?.siteId);

  const [sectionElement, setSectionElement] = React.useState<JSX.Element>();
  const [minItems, setMinItems] = React.useState<number>(5);
  const [loadSection, setLoadSection] = React.useState(false);
  // While a Show-less collapse is animating, keep the extra rows mounted so the
  // shrinking max-height has content to clip. Otherwise those rows unmount in the
  // same commit that shrinks max-height, leaving the transition nothing to
  // animate — the collapse snaps shut (expand looks smooth only because its extra
  // rows are added while max-height grows). Cleared once the transition ends.
  const [collapseAnimating, setCollapseAnimating] = React.useState(false);
  const collapseTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  // Anchor used to keep the section in view when it collapses (see showMoreClicked).
  const sectionHeaderRef = React.useRef<HTMLDivElement>(null);
  const [showFilterIcon, setShowFilterIcon] = React.useState(false);
  const [showFilterSearchBox, setFilterSearchBox] = React.useState(false);
  const [sectionFilterValue, setSectionFilterValue] = React.useState("");
  const [uniqueSearches, setUniqueSearches] = React.useState<string[]>([]);
  const [filterSearchValue, setFilterSearchValue] = React.useState<string>();

  const [dateRangeSteps, setDateRangeSteps] = React.useState<DateRangeStep[]>([
    { index: -5, label: languageStrings.FilterDateStepSince },
    { index: -4, label: languageStrings.FilterDateStepLast3Months, daysOffset: -92 },
    { index: -3, label: languageStrings.FilterDateStepLast30Days, daysOffset: -30 },
    { index: -2, label: languageStrings.FilterDateStepLast7Days, daysOffset: -7 },
    { index: -1, label: languageStrings.FilterDateStepSinceYesterday, daysOffset: -1 },
    { index: 0, label: languageStrings.FilterDateStepToday, daysOffset: 0 },
    { index: 1, label: languageStrings.FilterDateStepTomorrow, daysOffset: 1 },
    { index: 2, label: languageStrings.FilterDateStepIn7Days, daysOffset: 7 },
    { index: 3, label: languageStrings.FilterDateStepIn30Days, daysOffset: 30 },
    { index: 4, label: languageStrings.FilterDateStepIn3Months, daysOffset: 92 },
    { index: 5, label: languageStrings.FilterDateStepIn3MonthsOrMore, daysOffset: 93 }
  ]);

  const [selectedDateRangeStep, setSelectedDateRangeStep] = React.useState<DateRangeStep>(dateRangeSteps[0]);
  const [sliderValue, setSliderValue] = React.useState<number>(selectedDateRangeStep.index);
  const [hasFutureDateValues, setHasFutureDateValues] = React.useState<boolean>(false);

  const initializedAutoOpenSearchRef = React.useRef<string | null>(null);

  const isMultiSelectBoundField: boolean = React.useMemo(() => {
    try {
      // Injected-searcher (aggregate) mode has no browsed directory to scope the
      // datasource lookup, and its Eq-based filter pipeline lacks the
      // datasourceMutated Contains semantics the split relies on — splitting there
      // produces comma-fragments that can never match a stored "A, B" value.
      if (searchFilterValues) {
        return false;
      }
      if (filterSection?.filterField?.FieldType === "Text" && Array.isArray(datasourceItems)) {
        const datasourceField = DatasourceHelper.getInputDataSource(datasourceItems, filterSection.filterField.DisplayName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes);
        if (Array.isArray(datasourceField?.boundFields) && datasourceField.boundFields.length > 0) {
          const boundedField = datasourceField.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === filterSection.filterField.DisplayName.trim().toLowerCase()));
          if (boundedField.multiSelect) {
            return true;
          }
        }
      }
    } catch (error) {
      // ignore
    }
    return false;
  }, [datasourceItems, filterSection, searchFilterValues, clientTypes]);

  // Clear any pending collapse-unmount timer on unmount.
  React.useEffect(() => () => clearTimeout(collapseTimerRef.current), []);

  const onChange = (value: number) => {
    if (value !== selectedDateRangeStep.index) {
      const dateRangeStep = dateRangeSteps.find(x => x.index === value);
      setSelectedDateRangeStep(dateRangeStep);
      setSliderValue(value);
    }
  };

  const onChanged = (event: any, value: number, range?: [number, number]) => {
    if (selectedDateRangeStep.index !== value) {
      const dateRangeStep = dateRangeSteps.find(x => x.index === value);
      if (isInContentManagerMode) {
        dispatch(setContentManagerFilterSectionItemsFromRange(filterSection, dateRangeStep.daysOffset));
        dispatch(toggleReload());
      }
      else {
        setFilterSectionItemsFromRange(filterSection, dateRangeStep.daysOffset);
      }
    }
  };

  React.useEffect(() => {
    let debounceTimer: NodeJS.Timeout;
    if (sectionFilterValue?.length > 1) {
      debounceTimer = setTimeout(() => {
        const searchValue = sectionFilterValue.toLowerCase();
        const minItemsBeforeAdditionalSearch = 6;
        if (filterSection.items?.length > minItemsBeforeAdditionalSearch && sectionFilterValue.length > 0 && !uniqueSearches.includes(searchValue)) {
          setFilterSearchValue(sectionFilterValue);
        }
      }, 150);
    }

    const currentFilterSection = filterSections.find(x => x.filterField.RealFieldName === filterSection.filterField.RealFieldName)
    if (currentFilterSection) {
      if (currentFilterSection.filterField.Type === 'DateTime') {
        const selectedDateRange = (!currentFilterSection.daysOffset && currentFilterSection.daysOffset !== 0) ? dateRangeSteps[0] : dateRangeSteps.find(x => x.daysOffset === currentFilterSection.daysOffset);
        setSelectedDateRangeStep(selectedDateRange);
        setSliderValue(selectedDateRange.index);

        let hasFutureDates: boolean = false;
        if (currentFilterSection.items.length > 0) {
          try {
            const filterDate = new Date(currentFilterSection.items[currentFilterSection.items.length - 1].value);
            const filterTimeStamp = filterDate.getTime();
            if (!isNaN(filterTimeStamp) && filterTimeStamp > (new Date()).getTime()) {
              hasFutureDates = true;
              setHasFutureDateValues(true);
            }
          } catch (error) {
            // Ignore
          }
        }

        if (!hasFutureDates) {
          setDateRangeSteps(dateRangeSteps.slice(0, 6));
        }
      }

      const itemHeight: number = 30;
      let containerHeight: number;

      if (currentFilterSection.items.length > minItems && currentFilterSection.showMoreExpanded === false) {
        containerHeight = itemHeight * minItems;
      }
      else {
        containerHeight = itemHeight * currentFilterSection.items.length;
      }

      const shouldShowFilterTools = !currentFilterSection.showSlider && currentFilterSection.items.length > minItems;
      if (showFilterIcon !== shouldShowFilterTools) {
        setShowFilterIcon(shouldShowFilterTools);
      }

      if (shouldShowFilterTools && autoOpenFilterSearchbox && initializedAutoOpenSearchRef.current !== currentFilterSection.filterField.RealFieldName) {
        setFilterSearchBox(true);
        initializedAutoOpenSearchRef.current = currentFilterSection.filterField.RealFieldName;
      }

      const sectionStyle = mergeStyles({
        maxHeight: containerHeight - 10,
        overflow: "hidden",
        transition: `max-height ${COLLAPSE_ANIMATION_MS}ms ease-in-out`,
      });

      // Rows stay rendered while expanded AND for the duration of a collapse
      // animation (collapseAnimating) so max-height has content to clip on the
      // way down. containerHeight above still targets the SHORT height during
      // that window, so the rows shrink out of view before they unmount.
      const renderAllRows = currentFilterSection.showMoreExpanded || collapseAnimating;

      if (currentFilterSection?.items?.length > 0) {
        setSectionElement(
          <Stack tokens={{ childrenGap: 10 }} className={sectionStyle}>
            {currentFilterSection.items
              .filter(x => x.title?.toLowerCase().includes(sectionFilterValue?.toLowerCase()))
              .slice(0, renderAllRows ? currentFilterSection.items.length : currentFilterSection.items.length > minItems ? minItems : currentFilterSection.items.length)
              .map((item, index) => {
                return (
                  <Checkbox key={`${item.title}-${index}`} styles={{ root: { lineHeight: 40 } }} onRenderLabel={() => _onRenderLabel(item)} checked={item.selected} onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, isChecked?: boolean) => _onCheckboxChange(ev, isChecked, item)} />
                )
              })}
          </Stack>
        );
      } else {
        setSectionElement(undefined);
      }

      setLoadSection(true);
    }

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [filterSection, sectionFilterValue, autoOpenFilterSearchbox, collapseAnimating]);

  React.useEffect(() => {
    let cancellableSharePointRestApiService: CancellableSharePointRestApi;
    // The injected searcher has no cancellation token; drop its result if a newer
    // search superseded this one (the legacy path relies on cancel() instead).
    let stale = false;

    if (filterSearchValue?.length > 0) {
      (async () => {
        try {
          let items: string[];
          if (searchFilterValues) {
            items = await searchFilterValues(filterSection.filterField.RealFieldName, filterSearchValue);
            if (stale) {
              return;
            }
          } else {
            cancellableSharePointRestApiService = new CancellableSharePointRestApi(currentDirectory?.webUrl);

            items = await cancellableSharePointRestApiService.getSharepointFilterValues(
              isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
              currentDirectory?.sharePointListId,
              serverRelPath,
              filterSection.filterField.RealFieldName,
              filterSearchValue
            );
          }

          setUniqueSearches(prevSearches => [...prevSearches, filterSearchValue.toLowerCase()]);

          const oldSectionItems = [...filterSection.items];
          const newSectionItems: FilterItem[] = [...filterSection.items];

          const existingValues = new Set(filterSection.items.map(item => item.value?.toUpperCase()));

          items.filter(Boolean).forEach(item => {
            const filterValues: string[] = isMultiSelectBoundField
              ? item.split(",").map(v => v.trim()).filter(Boolean)
              : [item];

            filterValues.forEach(filterValue => {
              const upperCaseFilterValue = filterValue.toUpperCase();

              if (!existingValues.has(upperCaseFilterValue)) {
                existingValues.add(upperCaseFilterValue);

                newSectionItems.push({
                  title: filterValue,
                  value: filterValue,
                  selected: false
                });
              }
            });
          });

          if (newSectionItems?.length > oldSectionItems?.length) {
            const isBoekjaar = filterSection.filterField.FieldType === "Number" &&
              (filterSection.filterField.RealFieldName === "Boekjaar" || filterSection.filterField.DisplayName?.toLowerCase() === "boekjaar");

            const sectionItemsSorted = newSectionItems
              .sort((a, b) => {
                if (isBoekjaar) {
                  const aNum = +a.value;
                  const bNum = +b.value;
                  if (isNaN(aNum) && isNaN(bNum)) return 0; // Both are bad, keep original order
                  if (isNaN(aNum)) return 1;                // A is bad, push it to the end
                  if (isNaN(bNum)) return -1;               // B is bad, push it to the end
                  // Both are good, sort in descending order
                  return bNum - aNum;
                }

                return a.title?.toLowerCase() > b.title?.toLowerCase() ? 1 : -1;
              })
              .map((item, index) => {
                return {
                  ...item,
                  index
                }
              });

            if (isInContentManagerMode)
              dispatch(setContentManagerFilterSectionItems(filterSection, sectionItemsSorted));
            else
              setFilterSectionItems(filterSection, sectionItemsSorted);
          }
        } catch (error) {
          logger(error, "CMFileFilterPanelSection.tsx (Search filters)");
        }
      })();
    }

    return () => {
      stale = true;
      cancellableSharePointRestApiService?.cancel(currentDirectory?.webUrl);
    };
  }, [filterSearchValue]);

  const _onRenderLabel = (item: FilterItem) => {
    // A pre-selected value confirmed absent from the current results (existence
    // probe / empty grid) is shown struck-through so the user sees exactly what
    // the deep-link asked for and can clear it.
    const titleContent = item.unavailable
      ? (
        <TooltipHost content={languageStrings.NoResultsForValue}>
          <Stack.Item styles={checkboxLabelStyles} style={{ textDecoration: 'line-through', opacity: 0.6 }}>{item.title}</Stack.Item>
        </TooltipHost>
      )
      : <Stack.Item styles={checkboxLabelStyles}>{item.title}</Stack.Item>;
    return (
      <div style={{ paddingLeft: '5px', lineHeight: '20px' }} >
        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
          {filterSection.filterField.RealFieldName === 'DocIcon' && <Icon {...getFileTypeIconProps({ extension: item.fileType, size: 16, imageFileType: "png" })} />}
          {titleContent}
        </Stack>
      </div>
    )
  }

  function _onCheckboxChange(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, isChecked?: boolean, item?: FilterItem) {

    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionItem(filterSection, item, isChecked));
      dispatch(toggleReload());
    } else {
      setFilterSectionItem(filterSection, item, isChecked);
    }
  }

  const showMoreClicked = () => {
    // Expanding is the other "show me this section's values" gesture. Same one-edge
    // rule as onFilterClick — collapsing asks for nothing.
    if (!filterSection.showMoreExpanded) {
      onRequestFilterValues?.(filterSection.filterField.RealFieldName);
    }
    // Collapsing (currently expanded): hold the extra rows mounted across the
    // max-height transition so it animates shut instead of snapping. A small
    // buffer past the transition ensures the rows are fully clipped before they
    // unmount, so there is no visible pop. Expanding needs no such handling — its
    // rows are added as max-height grows and are revealed by the same transition.
    if (filterSection.showMoreExpanded) {
      setCollapseAnimating(true);
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = setTimeout(() => setCollapseAnimating(false), COLLAPSE_ANIMATION_MS + 50);
      // The toggle sits at the section's bottom, so collapsing removes height
      // ABOVE the current scroll position and the short section would otherwise
      // end up scrolled out of view above the viewport. Re-anchor its header;
      // block:"nearest" no-ops when the section is already fully visible.
      sectionHeaderRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionToggleShowMOre(filterSection))
    } else {
      setFilterSectionToggleShowMore(filterSection);
    }
  }

  const showDatesClicked = () => {
    const reloadGrid: boolean = selectedDateRangeStep.index !== dateRangeSteps[0].index;
    setSelectedDateRangeStep(dateRangeSteps[0]);
    setSliderValue(0);
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionItemsFromRange(filterSection, dateRangeSteps[0].daysOffset));
      dispatch(setContentManagerFilterSectionToggleSlider(filterSection));
      if (reloadGrid)
        dispatch(toggleReload());
    }
    else {
      setFilterSectionItemsFromRange(filterSection, dateRangeSteps[0].daysOffset);
      setFilterSectionToggleSlider(filterSection, !reloadGrid);
    }
  }

  const showSliderClicked = () => {
    let reloadGrid = filterSection.items.some(x => x.selected);
    // Same anchoring problem as Show less: the link sits below the full date
    // list, so switching to the (short) slider removes height above the scroll
    // position and leaves the section scrolled out of view above the viewport.
    // Re-anchor the header; block:"nearest" no-ops when already visible.
    sectionHeaderRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionToggleSlider(filterSection));
      if (reloadGrid) {
        dispatch(toggleReload());
      }
    }
    else {
      setFilterSectionToggleSlider(filterSection, !reloadGrid);
    }
  }

  const getSectionSubtitle = () => {
    if (filterSection.showSlider) {
      if (selectedDateRangeStep.index === dateRangeSteps[0]?.index) {
        let hasEmptyItem = filterSection.items.some(x => isEmptyOnlyTitle(x.title));
        let firstItem = hasEmptyItem ? filterSection?.items[1] : filterSection?.items[0]
        let dateString = dayjs(firstItem?.value).isValid() ? dayjs(firstItem?.value).format("ll") : firstItem?.title
        return `(${selectedDateRangeStep.label} ${dateString})`;
      } else {
        return `(${selectedDateRangeStep.label})`;
      }
    }

    if (filterSection.items?.some(x => x.selected)) {
      return `(${filterSection.items?.filter(x => x.selected)?.length})`;
    }

    return '';
  }

  const onFilterClick = () => {
    // Only on the way OPEN: closing the box is not a request for more values, and
    // firing on both edges would double the fan-out for one round trip of intent.
    if (!showFilterSearchBox) {
      onRequestFilterValues?.(filterSection.filterField.RealFieldName);
    }
    setFilterSearchBox(prev => !prev);
    setSectionFilterValue("");
  }

  const onChangeFilter = React.useCallback(async (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    setSectionFilterValue(newValue);
  }, []);

  return (
    <>
      {loadSection && <>
        {filterSection?.items?.length > 0 &&
          <Stack horizontal tokens={horizontalGapStackTokens} verticalAlign="center" horizontalAlign="space-between">
            <StackItem>
              {/* Scroll anchor: this header is what a collapse re-centres on. */}
              <div ref={sectionHeaderRef}>
                <Label styles={labelStyles}>{filterSection.filterField.DisplayName} {getSectionSubtitle()}</Label>
              </div>
            </StackItem>
            {showFilterIcon &&
              <TooltipHost content={!showFilterSearchBox ? languageStrings.SearchSectionFilter.replace("{section}", filterSection.filterField.DisplayName) : languageStrings.ClearSearch}>
                <StackItem styles={filterButtonStackItemStyles}>
                  {/* <ActionButton iconProps={{ iconName: !showFilterSearchBox ? "Filter" : "ClearFilter" }} allowDisabledFocus onClick={onFilterClick} /> */}
                  {!showFilterSearchBox
                    ? <Filter20Regular style={{ cursor: "pointer" }} onClick={onFilterClick} />
                    : <FilterDismiss20Regular style={{ cursor: "pointer" }} onClick={onFilterClick} />}
                </StackItem>
              </TooltipHost>}
          </Stack>
        }
        {showFilterIcon && showFilterSearchBox && <SearchBox
          styles={searchBoxStyles}
          placeholder={languageStrings.SearchSectionFilter.replace("{section}", filterSection.filterField.DisplayName)}
          iconProps={searchIcon}
          underlined={true}
          onChange={onChangeFilter}
          showIcon={true}
        />}
        {!filterSection.showSlider && sectionElement &&
          <>
            {sectionElement}
            {filterSection?.items?.filter(x => x.title?.toLowerCase().includes(sectionFilterValue?.toLowerCase())).length > minItems
              && <div className="xb-show-more-action" style={{ marginTop: 10, marginLeft: 4 }} onClick={() => { showMoreClicked() }}>
                <FontIcon iconName={!filterSection.showMoreExpanded ? "ChevronDown" : "ChevronUp"} />
                <span>{!filterSection.showMoreExpanded ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
              </div>
            }
          </>}
        {filterSection.hasSlider && filterSection.showSlider && filterSection.items?.length > 0 && <Slider
          min={dateRangeSteps[0]?.index}
          max={dateRangeSteps[dateRangeSteps?.length - 1]?.index}
          value={sliderValue}
          showValue={false}
          snapToStep={false}
          onChange={onChange}
          onChanged={onChanged}
          originFromZero={hasFutureDateValues && sliderValue > dateRangeSteps[0]?.index}
        />}
        {filterSection.hasSlider && filterSection.showSlider && filterSection.items?.length > 0 && <Link styles={linkStyles} onClick={() => showDatesClicked()}>{languageStrings.SelectSpecificDates}</Link>}
        {filterSection.hasSlider && !filterSection.showSlider && <Link styles={linkStyles} onClick={() => showSliderClicked()}>{languageStrings.ShowDateRangeSlider}</Link>}
      </>}
    </>
  )
};
